import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import { createPublicToken } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import Dropdown from '../controls/DropDown';

/**
 * Generate Public Link Page.
 * @returns - A React element that represents the generate public link page.
 */
export default function GeneratePublicLinkPage(): ReactElement {
  const [publicLink, setPublicLink] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { devices, groupedData, clientData } = useData();
  const [filterType, setFilterType] = useState<{ type: 'device' | 'group'; id: number }>({
    type: 'device',
    id: 1
  });
  const [filterId, setFilterId] = useState<{ id: number; name: string } | null>(null);
  const [duration, setDuration] = useState<{ label: string; days: number }>({
    label: '7 Days',
    days: 7
  });
  /**
   * Generate public link by calling the API.
   */
  const handleGenerateLink = async (): Promise<void> => {
    try {
      if (!filterId) {
        toast.error('Please select a device or group');
        return;
      }
      setLoading(true);
      // Calculate validTill date based on duration
      const validTillDate = new Date();
      validTillDate.setDate(validTillDate.getDate() + duration.days);
      const validTill = validTillDate.toISOString();

      // Prepare payload based on filter type
      const payload = {
        deviceId: filterType.type === 'device' ? filterId.id : 0,
        groupId: filterType.type === 'group' ? filterId.id : 0,
        validTill
      };

      const response = await createPublicToken(payload);

      if (response.data?.id) {
        const sanitizedClientName = (clientData?.clientName || '')
          .replace(/[\s\W_]+/g, '')
          .toLowerCase();
        const currentHost = `https://${sanitizedClientName}.telosair.com`;
        const generatedLink = `${currentHost}?token=${response.data.id}`;
        setPublicLink(generatedLink);
        toast.success('Public link generated successfully');
      } else {
        toast.error('Failed to generate public link');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      toast.error('An error occurred while generating the link');
    } finally {
      setLoading(false);
    }
  };
  /**
   * Copy the public link to clipboard.
   */
  const handleCopyLink = (): void => {
    if (publicLink) {
      navigator.clipboard.writeText(publicLink);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div className="my-4">
      {/* Generate Public Link Section */}
      <div className="rounded bg-white dark:bg-container-500 p-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Generate Public Link
        </div>

        <div className="mt-6">
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            Generate a public link to share your dashboard view with others. The link will provide
            read-only access.
          </p>
          <div className="max-w-sm">
            <Dropdown
              label="Filter Type"
              name="filterType"
              options={[
                { type: 'device', id: 1 },
                { type: 'group', id: 2 }
              ]}
              field="type"
              selected={filterType}
              handleOptionsSelect={(e) =>
                setFilterType(
                  JSON.parse(e.target.value) as { type: 'device' | 'group'; id: number }
                )
              }
            />
          </div>
          <div className="max-w-sm">
            <Dropdown
              label={filterType.type === 'device' ? 'Device' : 'Group'}
              name="filterId"
              options={
                filterType.type === 'device'
                  ? devices.map((device) => ({
                      id: device.serialNumber,
                      name: device.deviceName
                    }))
                  : groupedData.map((group) => ({
                      id: group.groupId,
                      name: group.groupName
                    }))
              }
              field="name"
              selected={filterId}
              handleOptionsSelect={(e) =>
                setFilterId(JSON.parse(e.target.value) as { id: number; name: string })
              }
            />
          </div>
          <div className="max-w-sm">
            <Dropdown
              label="Link Expiry"
              name="duration"
              options={[
                { label: '1 Day', days: 1 },
                { label: '7 Days', days: 7 },
                { label: '15 Days', days: 15 },
                { label: '30 Days', days: 30 },
                { label: '1 Year', days: 365 },
                { label: 'Never', days: 3650 }
              ]}
              field="label"
              selected={duration}
              handleOptionsSelect={(e) =>
                setDuration(JSON.parse(e.target.value) as { label: string; days: number })
              }
            />
          </div>
          <div className="flex gap-4 items-center my-6">
            <Button
              name={loading ? 'Generating...' : 'Generate Public Link'}
              theme="primary"
              size="md"
              onClick={handleGenerateLink}
              loading={loading}
              disabled={loading}
            />
          </div>

          {publicLink && (
            <div className="mt-6">
              <div className="block text-gray-500 dark:text-gray-400 text-sm font-medium mb-2">
                Generated Public Link
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-gray-50 dark:bg-background-500 rounded border border-gray-200 dark:border-gray-700">
                  <p className="text-gray-700 dark:text-gray-300 text-sm break-all">{publicLink}</p>
                </div>
                <Button name="Copy" theme="primary" size="sm" onClick={handleCopyLink} />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                Share this link with anyone to give them read-only access to your dashboard.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
